/**
 * ☠️ EL CONTRATO DEL DOCUMENTO DE CORREO. LO QUE ACÁ SE DECIDA, SE DECIDE UNA SOLA VEZ.
 *
 * El cuerpo de un aviso de Créditos se redacta en `@react-email/editor` (TipTap v3) y se dibuja
 * DOS veces: en la vista previa del editor (React del navegador) y en el correo real (React Email,
 * en el servidor). No pueden ser el mismo componente —la web no puede importar `@idc-forms/email`
 * sin meter una segunda copia de React al bundle, que es el defecto que documenta el ADR 0030—,
 * así que el JSX está escrito dos veces por necesidad.
 *
 * Lo que NO puede estar escrito dos veces son las DECISIONES: qué tipos de nodo existen, qué marks
 * se honran, si una URL es segura, qué propiedad de estilo se dibuja y con qué valores, qué texto
 * sale de un chip de variable, cuál de los dos lugares donde llega la alineación es el que manda.
 * Todo eso vive acá, y los dos
 * renderizadores lo consumen. Si diverge, diverge en el color de un borde; no en si un párrafo se
 * manda o no.
 *
 * ⛔ ES EL ÚNICO CEREBRO. El de BlockNote se borró con el flip: no quedan consumidores del formato
 * viejo, y dejar dos cerebros vivos es dejar dos lugares donde tomar la misma decisión distinto.
 * `safeUrl` vivía allá y ahora vive acá — se MUDÓ, no se copió, así que no hay dos filtros de URL
 * que puedan divergir. `safeColor` y la lista blanca de estilos (C3) nacen acá por el mismo motivo:
 * un correo de marca no puede tener dos ideas de qué valor es dibujable.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * C2 — LAS FORMAS DE NODO, MEDIDAS
 *
 * Todo lo que sigue sale de `spike-fixtures/contract.json`: el `getJSON()` exacto de un documento
 * real con botón, imagen, listas, marks y alineación. No es lo que la documentación del paquete
 * promete; es lo que el editor escribió.
 *
 * ── `variable` (nuestra, custom) ─────────────────────────────────────────────
 *   { type: 'variable', attrs: { variable: string } }
 * Inline y ATÓMICA: no tiene hijos y su texto es el valor resuelto. En v1 lleva ESE attr y nada
 * más. Un literal `{{ x }}` tipeado a mano NO se resuelve: las variables del cuerpo son NODOS, no
 * texto con plantilla. (En una PROP de bloque sí son texto, pero el formato nuevo no tiene props
 * de bloque redactables.)
 *
 * ── `button` ─────────────────────────────────────────────────────────────────
 *   attrs { href, alignment: 'left'|'center'|'right', style, class }
 * La etiqueta son sus HIJOS de texto, no un attr. `href` pasa por `safeUrl`.
 * ⛔ El target `credit` del formato viejo MUERE: un botón siempre lleva su `href` escrito. El botón
 * «Ver expediente» lo pone el MARCO cuando el cuerpo no declara ningún `button`
 * (`emailDocDeclares(blocks, 'button')`), que es la misma regla de no repetir que ya tenía el
 * formato viejo — sólo que ahora se pregunta por el tipo de nodo del editor.
 *
 * ── `image` ──────────────────────────────────────────────────────────────────
 *   attrs { src, alt, width, height, alignment, href, style }
 * ☠️ El spike midió que el editor NO exporta `alignment` ni el `style` inline de una imagen: su
 * `renderToReactEmail` es la única extensión que no llama `inlineCssToJs(node.attrs.style)`
 * —dibuja sólo el estilo del tema (`plugins/image/extension.tsx`)—. Eso limita al EDITOR, no a
 * nosotros: el nodo persiste los DOS, y nuestro compilador server-side (C4) los honra. `src` pasa
 * por `safeUrl`, el `href` por `emailDocHref` (tokens primero, saneado después) y el `style` por
 * C3. El enlace vive en el NODO atómico —no en un mark—: es la forma exacta que escribe
 * `plugins/image/extension.tsx` y que edita el BubbleMenu del editor.
 *
 * ⚠️ Y su tamaño no está en el estilo: el panel de tamaño del Inspector escribe `width`/`height`
 * en los ATTRS cuando el nodo es una imagen —`SIZE_AS_ATTRIBUTES = ['image']`,
 * `ui/inspector/sections/size.tsx`— y en el `style` para todo el resto.
 *
 * ── `heading` / `paragraph` ──────────────────────────────────────────────────
 *   heading:   attrs { level, alignment, style, class }
 *   paragraph: attrs { alignment, style, class }
 * ☠️ LA ALINEACIÓN LLEGA DUPLICADA, Y NO PORQUE EL EDITOR LA ESCRIBA DOS VECES.
 * `setTextAlignment` (`utils/set-text-alignment.ts`) toca UN solo lugar —`attrs.align` si el nodo
 * lo tiene, si no `attrs.alignment`— y NUNCA mete `text-align` en `attrs.style`. El
 * `style: 'text-align: center;'` de los fixtures entró con el HTML pegado, junto con el resto de
 * la cadena. Son dos orígenes distintos que pueden decir cosas distintas.
 *
 * **La regla —medida en el export del editor— es: el attr EXPLÍCITO manda; si no dice nada, el
 * `text-align` del `style` SOBREVIVE.** El JSX de `paragraph`/`heading`/`blockquote`/`listItem` es
 * `{ ...tema, ...inlineCssToJs(attrs.style), ...getTextAlignment(attrs.align || attrs.alignment) }`:
 * la alineación va ÚLTIMA, así que pisa al `text-align` de la cadena — pero `getTextAlignment`
 * devuelve `{}` cuando el attr no es left/center/right (`utils/get-text-alignment.ts`), y ahí el
 * `text-align` de la cadena queda en pie. Ver `emailDocAlignStyle`.
 *
 * ── `link` (mark) ────────────────────────────────────────────────────────────
 *   attrs { href, target: '_blank', rel: 'noopener noreferrer nofollow', style, class, title }
 * Nos importan `href` y `style`. `target` y `rel` los fija el renderizador, no el documento: son
 * decisiones de cómo se abre un enlace, y no puede cambiarlas quien redacta.
 *
 * ⭐ Es el ÚNICO estilo que vive en un mark, y se honra porque el Inspector lo escribe: el panel de
 * enlace guarda ahí el color (`updateLinkColor`, `ui/inspector/hooks/use-link-mark.ts`) y el export
 * del editor lo dibuja (`extensions/link.tsx`). Ver `EmailDocInlineSegment.linkStyle`.
 *
 * ── listas ───────────────────────────────────────────────────────────────────
 *   bulletList / orderedList (attrs.start: number) / listItem
 * Son listas REALES y anidables: un `listItem` contiene `paragraph`s, no texto suelto. C4 las
 * dibuja como `ul`/`ol` con `li` adentro — no como un `ul` por ítem, que es lo que el formato
 * viejo hacía porque BlockNote guardaba cada ítem como un bloque hermano.
 *
 * ── `text` ───────────────────────────────────────────────────────────────────
 *   { type: 'text', text: string, marks?: [{ type, attrs? }] }
 * ☠️ EN TIPTAP EL TEXTO SON NODOS HOJA. Por eso el tope de tamaño de este formato cuenta NODOS
 * TOTALES y no bloques de primer nivel: un párrafo con diez pedazos de estilo distinto son once
 * nodos, y contar sólo el primer nivel dejaría pasar un árbol enorme.
 *
 * ── `container` — EL QUE NO ES OPCIONAL ──────────────────────────────────────
 * ☠️ MEDIDO EN `dist/extensions-*.mjs`: el editor registra un plugin `containerEnforcer` que en
 * CADA transacción envuelve todo lo que no sea `globalContent` dentro de UN nodo `container`. No
 * es un nodo que el autor inserte: es estructural y forzado. Los cuatro fixtures del spike tienen
 * el mismo primer nivel — `[globalContent, container]` — y no hay manera de obtener una lista
 * plana de párrafos sin sacar la extensión.
 *
 * Consecuencia directa para el contrato C1: `blocks` es, en la práctica, `[{ type: 'container',
 * … }]`. Por eso `container` está en la lista blanca como CONTENEDOR TRANSPARENTE: su renderer no
 * dibuja envoltorio propio (el marco ya pone el suyo) y sólo baja a sus hijos. Dejarlo afuera
 * habría hecho que TODO documento real saliera con un único `BLOCK_UNSUPPORTED` y el correo vacío.
 *
 * ── LA COMPOSICIÓN — `section`, columnas y `table` (carril G) ────────────────
 *
 * Los ocho tipos de MAQUETACIÓN que v1 dejó afuera entran acá, con los nombres EXACTOS que el
 * editor emite —verificados contra `__fixtures__/editor-contract.json` y contra
 * `extensions/{section,columns,table}.tsx` del repo de React Email—.
 *
 *   section:      attrs { style, class }                                 → hijos `block+`
 *   twoColumns:   attrs { cellspacing, align, width, height, class, … }  → hijos `columnsColumn`
 *   threeColumns: idem                                                   → 3 `columnsColumn`
 *   fourColumns:  idem                                                   → 4 `columnsColumn`
 *   columnsColumn:attrs { style, alignment, align, width, height, … }    → hijos `block+`
 *   table:        attrs { style, align, width, border, cellpadding, cellspacing, … } → `tableRow+`
 *   tableRow:     attrs { style, valign, bgcolor, colspan, rowspan, … }  → `tableCell+`
 *   tableCell:    attrs { style, align, valign, width, … }               → hijos `block+`
 *
 * ⚠️ `fourColumns` NO está en el fixture del spike —el documento que se armó tenía dos y tres
 * columnas— pero SÍ en el editor: `VARIANTS` de `extensions/columns.tsx` lo registra igual que a
 * sus hermanas y `insertColumns(4)` lo inserta. Dejarlo afuera sería que un bloque que la barra
 * ofrece salga con `BLOCK_UNSUPPORTED`.
 *
 * ── ☠️ DÓNDE VIVE CADA MEDIDA, QUE NO ES DONDE UNO ESPERA ────────────────────
 *
 *  · El ANCHO de una columna y de una celda vive en el `style`, no en `attrs.width`:
 *    `SIZE_AS_ATTRIBUTES = ['image']` (`ui/inspector/sections/size.tsx`), o sea que el panel de
 *    tamaño escribe `width` en los ATTRS **sólo** para la imagen. El fixture lo confirma:
 *    `columnsColumn` con `attrs.width: null` y `style: 'width: 50%;'`.
 *  · La SEPARACIÓN entre columnas vive en un ATTR, `cellspacing`, y la escribe su propio panel
 *    (`ui/inspector/sections/column-spacing.tsx`, un `NumberInput` en px). En el lienzo el editor
 *    la dibuja como `gap`; en el correo, como el atributo `cellspacing` de la tabla. Ver
 *    `emailDocCellSpacing`.
 *  · Una `table` puede traer su ancho en el `style` y el serializador del editor lo PROMUEVE al
 *    atributo `width` de la `<table>` antes de dibujar —`node.type === 'table' &&
 *    inlineStyles.width && !node.attrs?.width` en `core/serializer/compose-react-email.tsx`—.
 *    Ver `emailDocTableWidth`.
 *
 * ── ⛔ `tableHeader` QUEDA AFUERA, Y NO POR DESCUIDO ─────────────────────────
 *
 * El schema del editor lo tiene (`tableRow` acepta `tableCell | tableHeader`) y un `<th>` pegado
 * lo crea. Pero se declara con `Node.create` y no con `EmailNode.create`, así que **no tiene
 * `renderToReactEmail`**: el propio serializador del editor lo tira —`if (!emailNode ||
 * !(emailNode instanceof EmailNode)) return null`—. O sea que ni la vista previa del editor ni el
 * correo saben dibujarlo. Fuera de la lista blanca hacemos exactamente lo mismo que el editor, y
 * además lo ASENTAMOS: `BLOCK_UNSUPPORTED` deja rastro de la fila de encabezado que se perdió, que
 * es más de lo que hoy da el editor.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Los tipos de nodo que un correo sabe dibujar. Lo que no está acá NO se dibuja, y queda asentado.
 *
 * Lista BLANCA y PLANA a propósito: es extensible agregando un renglón acá y su renderer en el
 * registro de C4, sin tocar ninguna otra pieza.
 *
 * ⭐ Los ocho de MAQUETACIÓN entraron con el carril G, y con ellos cambió el alcance: el cuerpo ya
 * no es sólo texto, es un correo COMPUESTO —la caja de nota anidada, la tabla de datos, la franja
 * de columnas del encabezado—. El marco sigue poniendo el logo, la ficha del expediente y el pie;
 * lo que dejó de poner es la única forma que el autor tenía de maquetar adentro.
 */
export const EMAIL_DOC_NODE_TYPES = [
  /** Estructural forzado por el editor. Transparente: sólo baja a sus hijos. Ver C2 arriba. */
  'container',
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'horizontalRule',
  'image',
  'button',
  'hardBreak',
  'text',
  /** La única pieza custom que sobrevive a la migración. */
  'variable',
  /* Maquetación (carril G). `tableHeader` queda afuera a propósito: ver C2. */
  'section',
  'twoColumns',
  'threeColumns',
  'fourColumns',
  'columnsColumn',
  'table',
  'tableRow',
  'tableCell'
] as const
export type EmailDocNodeType = (typeof EMAIL_DOC_NODE_TYPES)[number]

export const isEmailDocNodeType = (type: string): type is EmailDocNodeType =>
  (EMAIL_DOC_NODE_TYPES as readonly string[]).includes(type)

/**
 * Los marks que un correo honra.
 *
 * El editor produce además `code` y `uppercase` (medidos en el contrato). Quedan afuera: no los
 * ofrece ninguna de las plantillas que hay que replicar, y un mark que se dibuja «casi igual» es
 * peor que uno que no se dibuja — un mark desconocido NO se lleva el texto, sólo su estilo.
 *
 * ⚠️ Y `uppercase` queda afuera aunque las MAYÚSCULAS sí se ofrezcan: se dicen con
 * `textTransform` dentro de `preservedStyle`, que además sabe decir `capitalize`, `lowercase` y
 * `none`. Ninguna de las dos formas se puede escribir sin la otra en el mismo documento porque el
 * mark del kit NO está registrado en las extensiones de este editor: ver `EMAIL_DOC_STYLE_PROPS`.
 */
export const EMAIL_DOC_MARK_TYPES = [
  'bold',
  'italic',
  'underline',
  'strike',
  'link',
  /**
   * ⭐ EL ESTILO DE UNA SELECCIÓN. Se llama así porque así se llama en el paquete, no porque el
   * nombre describa bien lo que hace hoy. Ver `EMAIL_DOC_TEXT_STYLE_PROPS`.
   */
  'preservedStyle'
] as const
export type EmailDocMarkType = (typeof EMAIL_DOC_MARK_TYPES)[number]

export const isEmailDocMarkType = (type: string): type is EmailDocMarkType =>
  (EMAIL_DOC_MARK_TYPES as readonly string[]).includes(type)

/**
 * Topes duros del formato. Un documento guardado no debería llegar con miles de nodos.
 *
 * `nodes` cuenta NODOS TOTALES, no bloques de primer nivel: en TipTap el primer nivel es siempre
 * un solo `container` (ver C2), así que un tope por primer nivel no acotaría nada.
 *
 * ☠️ `depth: 16` MEDIDO, no elegido de memoria — y ES LA SEGUNDA MEDICIÓN. El formato viejo topaba
 * en 6; el documento del contrato del spike llega a 7 (`container > section > table > tableRow >
 * tableCell > paragraph > text`) y una lista anidada de v1 también, así que el tope se puso en 10.
 * Con la COMPOSICIÓN habilitada (carril G) ese 10 pasó a rechazar documentos legítimos:
 *
 *  · El editor deja anidar TRES niveles de columnas —`MAX_COLUMNS_DEPTH = 3` en
 *    `extensions/columns.tsx`, un tope suyo, no nuestro— y cada nivel son DOS niveles de árbol
 *    (el padre `twoColumns` y su `columnsColumn`). Seis niveles sin una sola letra escrita:
 *    `container > cols > col > cols > col > cols > col > paragraph > text` ya son **9**.
 *  · La caja de nota del correo de apartado es una `section` DENTRO de otra `section`, y la ficha
 *    de datos es una `table`. La cadena completa que el editor permite armar hoy en una pantalla:
 *    `container > section > section > cols > col > cols > col > cols > col > table > tableRow >
 *    tableCell > paragraph > text` = **14**.
 *
 * 16 es esa cadena más dos niveles de aire. Sigue siendo un GUARDARRAÍL contra un árbol torcido
 * —no contra la maquetación— y el que muerde en la práctica es `nodes`: las réplicas más grandes
 * del catálogo andan por los 80 nodos, cuarenta veces menos que el tope.
 *
 * ⚠️ `@idc-forms/schemas` ESPEJA estos dos números (`EMAIL_DOC_TREE_LIMITS`, `admin-credits.ts`)
 * porque no puede depender de este paquete. Si acá cambian, allá también — y el spec de este
 * archivo es lo que ata los dos valores al mismo fixture.
 */
export const EMAIL_DOC_LIMITS = { nodes: 3_000, depth: 16, inline: 500 } as const

/** Por qué un pedazo del correo no se pudo dibujar. Va a la bitácora, no al correo. */
export const EMAIL_DOC_ISSUE = {
  /** Un tipo de nodo que el correo no dibuja. */
  BLOCK_UNSUPPORTED: 'CREDIT_EMAIL_BLOCK_UNSUPPORTED',
  /** Una imagen con un `src` que no es `https:` (o vacío). */
  IMAGE_REJECTED: 'CREDIT_EMAIL_IMAGE_REJECTED',
  /** Un botón sin destino utilizable: su `href` no pasó `safeUrl`. */
  BUTTON_REJECTED: 'CREDIT_EMAIL_BUTTON_REJECTED'
} as const
export type EmailDocIssueCode = (typeof EMAIL_DOC_ISSUE)[keyof typeof EMAIL_DOC_ISSUE]

export type EmailDocIssue = { code: EmailDocIssueCode; detail: string }

/**
 * Lo que el renderizador necesita saber que NO está en el documento.
 *
 * Type ABIERTO a propósito (principio de la casa: nada cerrado). Un consumidor nuevo agrega su
 * campo sin que los que ya existen se enteren.
 *
 * ⛔ NO lleva `facts`, `typeOf` ni `conditionPolicy`: el formato nuevo no tiene condicionales ni
 * fórmulas en el cuerpo. Un chip de variable imprime su valor LEGIBLE y nada más, así que el mapa
 * crudo que el formato viejo necesitaba para comparar ya no tiene a quién servir. Si algún día
 * vuelve la condición, vuelve con su campo — no se deja el hueco abierto «por las dudas».
 */
export type EmailDocContext = {
  /**
   * Token → valor LEGIBLE. Es lo que imprime un chip: «Aprobado», «Q 1,000.00», «31/01/2026».
   *
   * ⭐ Y desde la tanda 1 es TODO lo que el cuerpo necesita saber del expediente. `creditUrl` y
   * `dossierRows` vivían acá porque los consumía el MARCO —el que ponía la ficha y el botón «Ver
   * expediente» por su cuenta—; con el marco vuelto contenido no hay quién los lea, y la misma
   * información entra por donde entra el resto: como variables del catálogo
   * (`expediente.enlace`, `expediente.codigo`, `expediente.etapa`, …). Dejarlos habría sido
   * mantener dos puertas para el mismo dato, y una de ellas cerrada al autor.
   */
  variables: Record<string, string>
  /** Dónde anotar lo que no se dibujó. No corta el envío. */
  onIssue?: (issue: EmailDocIssue) => void
}

/** Un nodo de TipTap tal como lo guarda el editor. Forma abierta: la define la librería. */
export type EmailDocNode = {
  type?: unknown
  attrs?: unknown
  text?: unknown
  marks?: unknown
  content?: unknown
}

export const asEmailDocRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/** Los `attrs` de un nodo, siempre un objeto. El editor los omite en `text` y en nodos pelados. */
export const emailDocAttrs = (node: unknown): Record<string, unknown> =>
  asEmailDocRecord(asEmailDocRecord(node).attrs)

/** Los hijos de un nodo, siempre un array. */
export const emailDocChildren = (node: unknown): Record<string, unknown>[] => {
  const content = asEmailDocRecord(node).content
  return Array.isArray(content) ? content.map(asEmailDocRecord) : []
}

/**
 * Sólo `https:`. Un `http:` lo bloquea el cliente de correo igual; un `javascript:` es otra cosa.
 *
 * ⚠️ Y por eso un `mailto:` tampoco pasa: el cuerpo de un aviso lo redacta un usuario y lo recibe
 * un cliente, así que el filtro es una lista blanca de UN protocolo, no una lista negra de los que
 * se nos ocurrieron. Un enlace rechazado no se lleva el texto — ver `EmailDocInlineSegment`.
 */
export const safeUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || value === '') return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

/**
 * El token de variable dentro de un `href`. La MISMA forma que reconoce el composer de la API
 * (`replaceNoticeTokens`), y a propósito: un destino que la vista previa resuelve y el envío no
 * sería un botón que funciona en pantalla y muere en la bandeja.
 */
const HREF_TOKEN = /\{\{\s*([a-zA-Z0-9._-]+)\s*\}\}/g

/** Un `href` con tokens es igual de largo que cualquier otro campo del documento. */
const HREF_MAX_LENGTH = 2_000

/**
 * ☠️ EL DESTINO DE UN BOTÓN O DE UN ENLACE: PRIMERO SE RESUELVE, DESPUÉS SE SANEA.
 *
 * El `href` del editor es un campo de texto libre y ahora puede llevar variables
 * —`{{ expediente.enlace }}` es lo que hace que el botón «Ver expediente» sea CONTENIDO y no algo
 * que el marco inyectaba—. Eso mete un paso nuevo, y **el orden de los dos pasos es el contrato**:
 *
 *  1. **Resolver** los tokens contra `variables`.
 *  2. **Sanear** el resultado con `safeUrl`, que sólo deja pasar `https:`.
 *
 * ⛔ Al revés no sirve de nada. Sanear la PLANTILLA —`{{ x }}`— es preguntarle a `new URL()` por
 * una cadena que todavía no es una URL: o la rechaza siempre (y ninguna variable funciona nunca)
 * o, si uno la deja pasar por ser plantilla, el valor real entra sin filtro. Saneando DESPUÉS, el
 * único texto que se juzga es el que va a viajar en el correo: una variable que resuelve a
 * `javascript:alert(1)` se descarta igual que si estuviera escrita a mano — que es la mitad
 * inversa que fija el spec.
 *
 * Un token sin valor sale VACÍO, igual que en el cuerpo: `https://app/x/{{ nada }}` se vuelve
 * `https://app/x/`, que sigue siendo un destino válido. Que quede un enlace a la raíz es mejor que
 * un `{{ … }}` crudo en un correo de marca — y es la misma decisión que ya toma el chip.
 */
export const emailDocHref = (value: unknown, variables: Record<string, string>): string | null => {
  if (typeof value !== 'string' || value === '' || value.length > HREF_MAX_LENGTH) return null
  const resolved = value.replace(HREF_TOKEN, (_, token: string) => variables[token] ?? '')
  return safeUrl(resolved)
}

/**
 * El tamaño que el nodo `image` puede entregar al atributo HTML.
 *
 * El Inspector oficial guarda números (píxeles implícitos), pero el renderer del editor pasa el
 * attr derecho a `<Img>` y también admite documentos importados con `px` o `%`. Por eso se
 * conservan las tres formas útiles y se rechaza TODO lo demás: una medida no es una puerta lateral
 * para meter `calc()`, unidades de viewport ni una cadena CSS arbitraria fuera de C3.
 *
 * Los límites son guardarraíles de correo, no de la imagen original: 4.000 px cubre con aire una
 * imagen retina para una hoja de 580 px y 100 % es el ancho completo del contenedor.
 */
export const emailDocImageSize = (value: unknown): number | string | undefined => {
  if (typeof value === 'number')
    return Number.isFinite(value) && value > 0 && value <= 4_000 ? Math.round(value) : undefined
  if (typeof value !== 'string') return undefined

  const candidate = value.trim()
  if (candidate === '' || candidate === 'auto') return undefined

  const match = /^(\d+(?:\.\d+)?)(px|%)?$/.exec(candidate)
  if (!match) return undefined
  const amount = Number(match[1])
  if (!Number.isFinite(amount) || amount <= 0) return undefined

  if (match[2] === '%') return amount <= 100 ? `${amount}%` : undefined
  if (amount > 4_000) return undefined
  return match[2] === 'px' ? `${amount}px` : Math.round(amount)
}

/* ────────────────────────────────────────────────────────────────────────────
 * C3 — EL ESTILO POR NODO: QUÉ SE HONRA Y POR QUÉ
 *
 * ⭐ Todo lo que sigue está MEDIDO en el código de `@react-email/editor` (repo oficial de React
 * Email, `packages/editor/src/**`). No es lo que la documentación promete: es lo que el Inspector
 * escribe y lo que el export dibuja. Cada renglón de la lista blanca cita su archivo.
 *
 * ── ☠️ `attrs.style` NO ES «lo que el Inspector escribió» ─────────────────────
 *
 * Es una cadena CSS CRUDA, y entra por DOS puertas:
 *
 *  1. El Inspector, que la reescribe entera con `jsToInlineCss` en cada cambio
 *     (`ui/inspector/utils/style-updates.ts` → `customUpdateStyles`). Formato: `prop:valor;`,
 *     kebab-case, sin espacio después del `:`.
 *  2. **HTML PEGADO.** `StyleAttribute.parseHTML` es literalmente
 *     `element.getAttribute('style') || ''` (`extensions/style-attribute.tsx`): pegar un pedazo de
 *     cualquier página web mete su `style` ENTERO, sin filtrar, dentro del documento. Los dos
 *     fixtures del spike llegaron así — por eso traen `display`, `vertical-align` y
 *     `width: fit-content`, que el Inspector no sabe escribir.
 *
 * Y ni siquiera la puerta 1 valida: el campo de texto del `ColorInput`
 * (`ui/inspector/primitives/color-input.tsx`) manda lo que se tipee, TAL CUAL, sin pasar por
 * `isValidHexColor`. O sea: la cadena que termina pintando un correo de marca que recibe un cliente
 * sale de un campo de texto libre. Ese es el motivo de esta lista blanca, y es el mismo motivo de
 * `safeUrl`.
 *
 * ── LO QUE SE DESCARTA NO SE ASIENTA ─────────────────────────────────────────
 *
 * Una propiedad fuera de la lista se cae en SILENCIO: no lleva `EmailDocIssue`. Los issues existen
 * para el CONTENIDO que no llegó —un párrafo, una imagen, un botón—, no para un adorno. Un correo
 * con el color equivocado se manda; uno con un párrafo menos, no debería.
 *
 * ── LO QUE QUEDA AFUERA, A PROPÓSITO ─────────────────────────────────────────
 *
 *  · `align` — está en `SUPPORTED_CSS_PROPERTIES` pero **no es una propiedad CSS**: es el prop
 *    `align` del `Container`/`Column` de React Email. Meterlo en un `style` no hace nada.
 *  · `fontFamily` — la tipografía es de la MARCA. La pone el marco y el tema global no se persiste
 *    (`globalContent` está excluido del schema); dejar que un nodo la pise sería que el autor
 *    cambie la marca desde un campo de texto.
 *  · Todo lo demás: `position`, `background` (su shorthand acepta `url()`), `zIndex`, `float`,
 *    `content`… Ninguna la escribe el Inspector y ninguna la sostiene un cliente de correo.
 * ──────────────────────────────────────────────────────────────────────────── */

/** El estilo SANO de un nodo: claves camelCase (las de React), valores ya validados. */
export type EmailDocStyle = Record<string, string>

/**
 * Los colores con NOMBRE que se aceptan: los 16 básicos de CSS, más `transparent`.
 *
 * ☠️ No es cosmética: `convertBorderValue` del editor (`utils/styles.ts`) rellena el color de un
 * `border` incompleto con la cadena `'black'`, y el campo de texto del `ColorInput` deja tipear
 * `red`. Sin esta lista, un borde legítimo del propio editor se caería.
 */
const EMAIL_DOC_NAMED_COLORS = [
  'black',
  'silver',
  'gray',
  'white',
  'maroon',
  'red',
  'purple',
  'fuchsia',
  'green',
  'lime',
  'olive',
  'yellow',
  'navy',
  'blue',
  'teal',
  'aqua',
  'transparent',
  'currentcolor'
] as const

/** `#rgb`, `#rrggbb`, `#rrggbbaa` — los tres que `isValidHexColor` del editor acepta. */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/**
 * `rgb()/rgba()/hsl()/hsla()` con SÓLO números adentro.
 *
 * La clase de caracteres es la defensa: sin letras ni paréntesis anidados adentro, `rgb(url(x))` y
 * cualquier otra función disfrazada no entran. El comentario de `getStyle`
 * (`ui/inspector/node.tsx`) confirma que el editor espera valores como `hsl(200, 50%, 40%)`.
 */
const FUNCTIONAL_COLOR = /^(?:rgb|rgba|hsl|hsla)\([\d.,%/\s+-]{1,60}\)$/i

/**
 * Un color utilizable, o `null`.
 *
 * Es a los colores lo que `safeUrl` a los destinos: lista BLANCA de formas, no lista negra de
 * ataques. Lo que no es hex, ni un nombre de la lista, ni una función numérica, no se dibuja.
 */
export const safeColor = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (raw === '' || raw.length > 64) return null
  if (HEX_COLOR.test(raw)) return raw
  const lower = raw.toLowerCase()
  if ((EMAIL_DOC_NAMED_COLORS as readonly string[]).includes(lower)) return lower
  return FUNCTIONAL_COLOR.test(raw) ? raw : null
}

/**
 * El tope por unidad. Un `padding: 99999px` no es un estilo: es un correo roto.
 *
 * `%` llega a 1000 porque `lineHeight` usa `%` como unidad en `SUPPORTED_CSS_PROPERTIES` con 155
 * de default; `px` llega a 2000 porque el ancho del marco son 580 y una imagen retina puede pedir
 * el doble. No son números finos: son el orden de magnitud arriba del cual el valor ya no puede
 * venir de alguien maquetando un correo.
 */
const LENGTH_UNIT_MAX: Record<string, number> = { px: 2_000, '%': 1_000, em: 100, rem: 100 }

/** Un número sin unidad (`lineHeight: 1.5`, `fontWeight: 700`). */
const UNITLESS_MAX = 1_000

const LENGTH = /^(-?\d+(?:\.\d+)?)(px|%|em|rem)?$/

/**
 * Una longitud: número + `px|%|em|rem`, o uno de los keywords que la prop admita.
 *
 * ⚠️ Sin unidad SÓLO se acepta el cero. `padding: 8` no lo dibuja ningún cliente de correo, así que
 * dejarlo pasar sería escribir una declaración muerta.
 */
const safeLength = (value: string, keywords: readonly string[]): string | null => {
  const raw = value.trim()
  const lower = raw.toLowerCase()
  if (keywords.includes(lower)) return lower
  const match = LENGTH.exec(raw)
  if (!match) return null
  const amount = Number(match[1])
  if (!Number.isFinite(amount)) return null
  const unit = match[2]
  if (!unit) return amount === 0 ? raw : null
  const max = LENGTH_UNIT_MAX[unit]
  return max !== undefined && Math.abs(amount) <= max ? raw : null
}

/** Un número sin unidad, o una longitud. Es la forma de `lineHeight`: `1.5`, `18px` y `155%`. */
const safeNumberOrLength = (value: string): string | null => {
  const raw = value.trim()
  const amount = Number(raw)
  if (raw !== '' && Number.isFinite(amount) && Math.abs(amount) <= UNITLESS_MAX) return raw
  return safeLength(raw, [])
}

/** Cuántos valores puede llevar un shorthand de caja (`padding: 5px 10px 15px 20px`). */
const STYLE_MAX_TOKENS = 4

const tokensOf = (value: string): string[] => value.trim().split(/\s+/)

/**
 * Un shorthand de caja: de uno a cuatro valores, cada uno una longitud.
 *
 * ☠️ `auto` es uno de los keywords que `margin` admite, y no es un lujo: el editor centra una imagen
 * con `margin: 30px auto` —así llega el fixture real—. Sin `auto`, centrar deja de funcionar.
 */
const safeBox = (value: string, keywords: readonly string[]): string | null => {
  const tokens = tokensOf(value)
  if (tokens.length === 0 || tokens.length > STYLE_MAX_TOKENS) return null
  const safe = tokens.map((token) => safeLength(token, keywords))
  return safe.every((token): token is string => token !== null) ? safe.join(' ') : null
}

/** Los trazos de borde que ofrece el `select` del Inspector, más `none`. */
const BORDER_STYLE_KEYWORDS = ['solid', 'dashed', 'dotted', 'none'] as const

/**
 * El shorthand `border: 1px solid #253B6D` — tal cual llega en los documentos reales.
 *
 * Hasta tres pedazos, cada uno un ancho, un trazo o un color. No se reordena ni se expande a
 * longhands: el editor tampoco lo hace fuera de las tablas (`expandShorthandProperties` sólo lo usa
 * `extensions/table.tsx`), y expandir acá sería dibujar distinto que la vista previa.
 */
const safeBorder = (value: string): string | null => {
  const tokens = tokensOf(value)
  if (tokens.length === 0 || tokens.length > 3) return null
  const safe = tokens.map((token) => {
    const lower = token.toLowerCase()
    if ((BORDER_STYLE_KEYWORDS as readonly string[]).includes(lower)) return lower
    return safeLength(token, []) ?? safeColor(token)
  })
  return safe.every((token): token is string => token !== null) ? safe.join(' ') : null
}

/** Cómo se valida el valor de una propiedad. */
type EmailDocStyleRule =
  | { kind: 'color' }
  | { kind: 'length'; keywords?: readonly string[] }
  | { kind: 'box'; keywords?: readonly string[] }
  | { kind: 'number' }
  | { kind: 'keyword'; keywords: readonly string[] }
  | { kind: 'border' }

/**
 * ☠️ LA LISTA BLANCA DE ESTILOS. Lo que no está acá NO viaja al correo.
 *
 * Sale de tres mediciones que se suman, y cada grupo dice de dónde:
 *
 *  (a) **`SUPPORTED_CSS_PROPERTIES`** (`plugins/email-theming/themes.ts`) — el catálogo del
 *      Inspector. De ahí salen también las unidades (`fontSize` px, `lineHeight` %,
 *      `letterSpacing` px) y los valores de cada `select`.
 *  (b) **Los paneles que de verdad escriben** (`ui/inspector/sections/*.tsx`): Typography →
 *      `color`/`fontSize`/`lineHeight`; Background → `backgroundColor`; Border →
 *      `border{Width,Style,Color}` con sus cuatro lados y `borderRadius`; Spacing → los cuatro
 *      `padding*` en un solo lote; Size → `width`/`height` (⚠️ en `image` van a los ATTRS, no al
 *      style: `SIZE_AS_ATTRIBUTES = ['image']`).
 *  (c) **Lo que los documentos REALES traen** y el catálogo no tiene, porque entró con HTML pegado:
 *      `display`, `textAlign`, `textDecoration`, `fontWeight: bold`, `lineHeight: 1.5` sin unidad,
 *      `width: fit-content`, `margin`/`marginTop`, `border` y `borderBottom` como shorthand,
 *      `backgroundColor: transparent`. Sin este grupo, el botón del aviso de etapa reconstruido
 *      —el que hoy sale en producción— pierde su forma entera.
 */
export const EMAIL_DOC_STYLE_PROPS = {
  /* Tipografía — (a) catálogo + (b) TypographySection. */
  color: { kind: 'color' },
  fontSize: { kind: 'length' },
  lineHeight: { kind: 'number' },
  letterSpacing: { kind: 'length', keywords: ['normal'] },
  /** (a) `select` con 300/400/600/700/800; (c) los documentos reales traen `bold`. */
  fontWeight: {
    kind: 'keyword',
    keywords: [
      'normal',
      'bold',
      'lighter',
      'bolder',
      '100',
      '200',
      '300',
      '400',
      '500',
      '600',
      '700',
      '800',
      '900'
    ]
  },
  /** (a) `select` con none/underline/line-through. */
  textDecoration: { kind: 'keyword', keywords: ['none', 'underline', 'line-through'] },
  /**
   * ⭐ LAS MAYÚSCULAS, que no salen de ninguno de los tres grupos de arriba: entran por MEDICIÓN
   * del correo que hay que replicar. `reservation-declined` las usa OCHO veces —las etiquetas de
   * cada fila de la ficha de datos y el rótulo del botón—, y el ensayo de la tanda 7 las contó
   * como la única señal que quedaba en cero después de arreglar todo lo demás.
   *
   * ☠️ NO ESTÁ EN `SUPPORTED_CSS_PROPERTIES` DEL KIT, Y ASÍ Y TODO SE ESCRIBE. El catálogo del
   * Inspector sólo decide la UNIDAD que `customUpdateStyles` le pega a un número
   * (`ui/inspector/utils/style-updates.ts`): una propiedad que no está en el catálogo se escribe
   * igual, tal cual, porque el `for` de esa función no filtra por catálogo. Medido leyendo la
   * función entera — lo que se pierde afuera del catálogo son las unidades y los `select`, no la
   * escritura.
   *
   * ⚠️ El kit SÍ tiene otra forma de decir mayúsculas: un mark `uppercase`
   * (`extensions/uppercase.tsx`), que dibuja `<span style="text-transform: uppercase">`. Queda
   * afuera A PROPÓSITO y no está registrado en las extensiones de este editor
   * (`credit-email-doc-editor.tsx` las lista una por una): ofrece SÓLO mayúsculas, no tiene dónde
   * decir `capitalize` ni volver a `none`, y tener dos formas de escribir la misma declaración es
   * la receta para que el lienzo y el correo digan cosas distintas. Ver `EMAIL_DOC_MARK_TYPES`.
   */
  textTransform: {
    kind: 'keyword',
    keywords: ['none', 'uppercase', 'lowercase', 'capitalize']
  },
  /** (c) Las tres de `EMAIL_DOC_ALIGNMENTS`. `justify` no existe en este editor. */
  textAlign: { kind: 'keyword', keywords: ['left', 'center', 'right'] },

  /* Fondo y bordes — (a) catálogo + (b) BackgroundSection / BorderSection. */
  backgroundColor: { kind: 'color' },
  borderRadius: { kind: 'box' },
  borderTopLeftRadius: { kind: 'length' },
  borderTopRightRadius: { kind: 'length' },
  borderBottomLeftRadius: { kind: 'length' },
  borderBottomRightRadius: { kind: 'length' },
  borderWidth: { kind: 'box' },
  borderTopWidth: { kind: 'length' },
  borderRightWidth: { kind: 'length' },
  borderBottomWidth: { kind: 'length' },
  borderLeftWidth: { kind: 'length' },
  borderStyle: { kind: 'keyword', keywords: BORDER_STYLE_KEYWORDS },
  borderTopStyle: { kind: 'keyword', keywords: BORDER_STYLE_KEYWORDS },
  borderRightStyle: { kind: 'keyword', keywords: BORDER_STYLE_KEYWORDS },
  borderBottomStyle: { kind: 'keyword', keywords: BORDER_STYLE_KEYWORDS },
  borderLeftStyle: { kind: 'keyword', keywords: BORDER_STYLE_KEYWORDS },
  borderColor: { kind: 'color' },
  borderTopColor: { kind: 'color' },
  borderRightColor: { kind: 'color' },
  borderBottomColor: { kind: 'color' },
  borderLeftColor: { kind: 'color' },
  /** (c) `border: 1px solid #253B6D` y `border-bottom: 1px solid #F2F5F9`, de los fixtures. */
  border: { kind: 'border' },
  borderTop: { kind: 'border' },
  borderRight: { kind: 'border' },
  borderBottom: { kind: 'border' },
  borderLeft: { kind: 'border' },

  /* Caja — (a) catálogo (`margin`, `padding`, los cuatro `padding*`, `width`, `height`) +
   * (b) PaddingSection / SizeSection + (c) los cuatro `margin*`, que sólo entran por HTML pegado. */
  margin: { kind: 'box', keywords: ['auto'] },
  marginTop: { kind: 'length', keywords: ['auto'] },
  marginRight: { kind: 'length', keywords: ['auto'] },
  marginBottom: { kind: 'length', keywords: ['auto'] },
  marginLeft: { kind: 'length', keywords: ['auto'] },
  padding: { kind: 'box' },
  paddingTop: { kind: 'length' },
  paddingRight: { kind: 'length' },
  paddingBottom: { kind: 'length' },
  paddingLeft: { kind: 'length' },
  width: { kind: 'length', keywords: ['auto', 'fit-content', 'max-content', 'min-content'] },
  height: { kind: 'length', keywords: ['auto', 'fit-content', 'max-content', 'min-content'] },
  /** (c) `display: block` en el botón y en la imagen de los documentos reales. */
  display: { kind: 'keyword', keywords: ['block', 'inline-block', 'inline', 'none'] },

  /**
   * (c) **La medida de una celda.** Entra sólo por HTML pegado —no está en el catálogo del
   * Inspector ni hay panel que la escriba: `vertical-align` no aparece una sola vez en el código
   * del editor— y aun así los OCHO `tableCell` de la réplica del aviso de etapa la traen
   * (`vertical-align: top` en `__fixtures__/editor-credit-stage-notice.json`).
   *
   * ☠️ Sin ella, la etiqueta y el valor de cada fila de la ficha se centran verticalmente y la
   * tabla de datos deja de parecerse al correo que replica. Fue la última prop que quedó afuera de
   * la lista blanca de v1 —con el argumento de que `table` no estaba— y esa condición ya no vale.
   */
  verticalAlign: { kind: 'keyword', keywords: ['top', 'middle', 'bottom', 'baseline'] }
} as const satisfies Record<string, EmailDocStyleRule>

export type EmailDocStyleProp = keyof typeof EMAIL_DOC_STYLE_PROPS

/**
 * Las claves de la lista blanca, en un `Set`.
 *
 * ⚠️ No es una optimización: es la defensa contra las claves HEREDADAS. Preguntar
 * `prop in EMAIL_DOC_STYLE_PROPS` daría `true` para `constructor`, `toString` y `__proto__`, y esa
 * última es la que muerde: `out['__proto__'] = valor` en un objeto literal no crea una clave, le
 * cambia el prototipo. Un `Set` no tiene ninguna de esas claves.
 */
const EMAIL_DOC_STYLE_PROP_KEYS: ReadonlySet<string> = new Set(Object.keys(EMAIL_DOC_STYLE_PROPS))

export const isEmailDocStyleProp = (prop: string): prop is EmailDocStyleProp =>
  EMAIL_DOC_STYLE_PROP_KEYS.has(prop)

/**
 * Lo que NINGÚN valor puede contener, mire qué propiedad mire.
 *
 * Es el filtro que corre ANTES de la lista blanca, y existe porque una propiedad honrada con un
 * valor tramposo es peor que una propiedad desconocida: `background-color: url(...)` pasaría la
 * lista y saldría al correo. `var()` y `calc()` caen acá y no por peligrosas, sino porque el
 * documento no tiene custodios: sin el tema del editor —que no se persiste—, una `var()` que no
 * resuelve deja la declaración muerta y el estilo se ve distinto que en la vista previa.
 */
const UNSAFE_STYLE_VALUE =
  /url\(|expression\(|var\(|calc\(|attr\(|image-set\(|!important|javascript:|[<>"'\\{}@]/i

/** Tope de la cadena entera y de cuántas declaraciones se leen. Un `style` sano tiene menos de 10. */
const STYLE_MAX_LENGTH = 2_000
const STYLE_MAX_DECLARATIONS = 40

/**
 * Corta por `;` a nivel cero de paréntesis — el mismo algoritmo que `splitDeclarations` del editor
 * (`utils/styles.ts`), para que `rgb(0, 0, 0)` no se parta al medio.
 */
const splitDeclarations = (input: string): string[] => {
  const out: string[] = []
  let current = ''
  let depth = 0
  for (const char of input) {
    if (char === '(') depth++
    else if (char === ')') depth = Math.max(0, depth - 1)
    if (char === ';' && depth === 0) {
      out.push(current)
      current = ''
      if (out.length >= STYLE_MAX_DECLARATIONS) return out
    } else {
      current += char
    }
  }
  if (current !== '') out.push(current)
  return out
}

/** `background-color` → `backgroundColor`. Igual que `inlineCssToJs` del editor. */
const camelProp = (key: string): string =>
  key.trim().replace(/-\w/g, (match) => match[1].toUpperCase())

const safeStyleValue = (rule: EmailDocStyleRule, raw: string): string | null => {
  switch (rule.kind) {
    case 'color':
      return safeColor(raw)
    case 'length':
      return safeLength(raw, rule.keywords ?? [])
    case 'box':
      return safeBox(raw, rule.keywords ?? [])
    case 'number':
      return safeNumberOrLength(raw)
    case 'keyword': {
      const lower = raw.trim().toLowerCase()
      return rule.keywords.includes(lower) ? lower : null
    }
    case 'border':
      return safeBorder(raw)
  }
}

/**
 * Una cadena CSS cruda → el estilo SANO que se puede dibujar.
 *
 * Tres cortes, en este orden: la propiedad tiene que estar en la lista blanca, el valor no puede
 * traer nada de `UNSAFE_STYLE_VALUE`, y tiene que ser de la FORMA que esa propiedad admite. Una
 * declaración que no pasa se cae sola: no se lleva a las hermanas.
 *
 * ⚠️ La lista blanca se consulta con un `Set` y no con un `in`: ver `EMAIL_DOC_STYLE_PROP_KEYS`.
 */
export const emailDocStyle = (value: unknown): EmailDocStyle => {
  const out: EmailDocStyle = {}
  if (typeof value !== 'string' || value === '' || value.length > STYLE_MAX_LENGTH) return out

  for (const declaration of splitDeclarations(value)) {
    const trimmed = declaration.trim()
    if (trimmed === '') continue
    const separator = trimmed.indexOf(':')
    if (separator === -1) continue

    const prop = camelProp(trimmed.slice(0, separator))
    if (!isEmailDocStyleProp(prop)) continue

    const raw = trimmed.slice(separator + 1).trim()
    if (raw === '' || UNSAFE_STYLE_VALUE.test(raw)) continue

    const safe = safeStyleValue(EMAIL_DOC_STYLE_PROPS[prop], raw)
    if (safe !== null) out[prop] = safe
  }

  return out
}

/**
 * El estilo sano de un NODO: su `attrs.style`, pasado por la lista blanca.
 *
 * Los dos renderizadores lo fusionan igual: el estilo base del tipo primero, éste encima, prop a
 * prop. Lo que el autor tocó pisa lo que el tipo trae de fábrica, y lo que no tocó queda como
 * estaba —que es exactamente lo que hace el export del editor:
 * `{ ...style, ...inlineCssToJs(node.attrs?.style) }` (`extensions/paragraph.tsx` y hermanas)—.
 */
export const emailDocNodeStyle = (node: unknown): EmailDocStyle =>
  emailDocStyle(emailDocAttrs(node).style)

/* ────────────────────────────────────────────────────────────────────────────
 * C3-bis — EL ESTILO A NIVEL DE SELECCIÓN
 *
 * ☠️ EL PAQUETE NO TIENE UN MARK `textStyle`, Y NO ES UN DESCUIDO NUESTRO. Medido en
 * `@react-email/editor@1.7.3`, `packages/editor/src`: la cadena `textStyle` aparece UNA sola vez en
 * todo el código —`ui/inspector/hooks/use-document-colors.ts`, leyendo `mark.type.name ===
 * 'textStyle'` para juntar colores del documento—, y ninguna extensión con ese nombre se declara ni
 * se registra. Es una rama muerta que espera al `@tiptap/extension-text-style` que el kit no trae.
 *
 * ⛔ Y su Inspector TAMPOCO estiliza una selección. `InspectorText.setStyle`
 * (`ui/inspector/text.tsx`) es literalmente `updateParentBlockStyle(editor, parentPos, prop, value)`
 * (`ui/inspector/utils/text-block-utils.ts`), que hace `setNodeMarkup(parentPos, …)` sobre el
 * **bloque padre**. O sea: con una palabra seleccionada, elegir un color en el panel se lo aplica al
 * PÁRRAFO ENTERO. Eso es exactamente lo que se reportó, y es comportamiento del paquete.
 *
 * ── EL MARK QUE SÍ EXISTE ────────────────────────────────────────────────────
 *
 * `preservedStyle` (`extensions/preserved-style.tsx`), declarado con `EmailMark.create`:
 *
 *   { type: 'preservedStyle', attrs: { style: string } }
 *
 * Su `renderHTML` es `['span', mergeAttributes(HTMLAttributes), 0]` y su `renderToReactEmail` es
 * `<span style={inlineCssToJs(mark.attrs.style)}>{children}</span>`: una cadena CSS cruda que se
 * dibuja inline, la MISMA forma que `attrs.style` de un nodo. Por eso el saneado es el mismo
 * (`emailDocStyle`) y no un segundo filtro que pueda divergir.
 *
 * El kit lo escribe hoy por dos puertas: al PEGAR un `span[style]` con estilos preservables, y al
 * QUITAR un enlace (`unsetLink` → `.setMark('preservedStyle', { style: preservedStyle })`, en
 * `extensions/link.tsx`). Que el panel de texto también lo escriba sobre la selección es trabajo de
 * la instancia — el kit deja la puerta, no la cruza.
 *
 * ── ⛔ POR QUÉ NO SE HONRA LA LISTA BLANCA ENTERA ────────────────────────────
 *
 * Un `<span>` es INLINE. `padding`, `margin`, `width`, `height`, `border` y `display` en un span o
 * no hacen nada o rompen la maqueta, y en un cliente de correo hacen las dos cosas según el cliente
 * —que es peor que no hacer nada, porque se ve bien en la vista previa—. `textAlign` alinea un
 * bloque, no un pedazo. `verticalAlign` es de celda.
 *
 * Queda entonces lo que un pedazo de texto SÍ puede llevar, más `backgroundColor`, que es un
 * resaltado legítimo y además lo que el propio kit preserva al desenlazar un span con fondo
 * (`hasBackground`, `processStylesForUnlink`).
 *
 * ⭐ `textTransform` entra en la misma bolsa y por la misma vara: transforma las LETRAS del pedazo
 * que envuelve, no la caja, así que un `<span>` es exactamente su alcance. Es además la propiedad
 * que el correo a replicar usa ocho veces.
 *
 * ⛔ `fontFamily` queda afuera por el mismo motivo que en C3: la tipografía es de la MARCA.
 * ──────────────────────────────────────────────────────────────────────────── */

/** El nombre EXACTO del mark en `@react-email/editor`. No se renombra: se cita. */
export const EMAIL_DOC_TEXT_STYLE_MARK = 'preservedStyle' as const

/** Las propiedades que un pedazo de texto puede llevar. Subconjunto de C3, no una lista aparte. */
export const EMAIL_DOC_TEXT_STYLE_PROPS = [
  'color',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'textDecoration',
  'textTransform',
  'backgroundColor'
] as const satisfies readonly EmailDocStyleProp[]

export type EmailDocTextStyleProp = (typeof EMAIL_DOC_TEXT_STYLE_PROPS)[number]

const EMAIL_DOC_TEXT_STYLE_KEYS: ReadonlySet<string> = new Set(EMAIL_DOC_TEXT_STYLE_PROPS)

export const isEmailDocTextStyleProp = (prop: string): prop is EmailDocTextStyleProp =>
  EMAIL_DOC_TEXT_STYLE_KEYS.has(prop)

/**
 * Una cadena CSS cruda de un mark → el estilo SANO que un `<span>` puede llevar.
 *
 * Dos cortes, en este orden: primero la vara de C3 —la misma función, con sus formas y su filtro de
 * valores tramposos—, y encima el recorte a lo que aplica en un pedazo de texto. Escribirlo como
 * una segunda lista blanca habría sido dejar dos ideas de qué color es dibujable.
 */
export const emailDocTextStyle = (value: unknown): EmailDocStyle => {
  const out: EmailDocStyle = {}
  for (const [prop, safe] of Object.entries(emailDocStyle(value))) {
    if (isEmailDocTextStyleProp(prop)) out[prop] = safe
  }
  return out
}

/** El estilo de un pedazo de texto, derivado de sus marks. */
export type EmailDocInlineStyle = {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
}

/**
 * Un pedazo de texto ya resuelto: lo que el renderizador tiene que dibujar, y nada más.
 *
 * `href` es el destino cuando el pedazo lleva el mark `link`, y `null` cuando no —o cuando el
 * destino no pasó `safeUrl`—.
 *
 * ☠️ QUE EL DESTINO SE CAIGA NO SE LLEVA EL TEXTO. Es la regla que el formato viejo aprendió a la
 * mala (un `link` sin hijos devolvía `''` y el texto del enlace desaparecía entero) y la que este
 * formato hereda: un `href` inseguro sale como texto plano, nunca como hueco.
 */
export type EmailDocInlineSegment = {
  key: string
  text: string
  style: EmailDocInlineStyle
  href: string | null
  /**
   * El estilo SANO del mark `link` que envuelve este pedazo, o `null` cuando no lleva ninguno.
   *
   * ⭐ Es el único estilo que vive en un MARK y no en un nodo, y está acá porque el Inspector lo
   * escribe: el panel de enlace (`ui/inspector/text.tsx` → `LinkSection`) llama a `updateLinkColor`
   * (`ui/inspector/hooks/use-link-mark.ts`), que reescribe `attrs.style` del mark con
   * `jsToInlineCss`. El export del editor lo dibuja (`{ ...tema, ...inlineCssToJs(mark.attrs.style)
   * }` en `extensions/link.tsx`), así que el correo también: un color de enlace que se elige en el
   * panel y no llega al correo es exactamente el WYSIWYG mentiroso que este formato existe para
   * cerrar.
   *
   * ⚠️ El `href` NO sale de acá: sale de `href`, ya filtrado. Un mark con estilo pero con destino
   * inseguro sigue saliendo como TEXTO PLANO — el estilo no reabre la puerta que `safeUrl` cerró.
   */
  linkStyle: EmailDocStyle | null
  /**
   * El estilo SANO del mark `preservedStyle` que envuelve este pedazo, o `null` cuando no lleva
   * ninguno. Es el estilo a nivel de SELECCIÓN: lo que distingue una palabra en color del párrafo
   * entero en color.
   *
   * ⚠️ El nombre del campo dice qué ES; el del mark dice cómo se llama en el paquete
   * (`EMAIL_DOC_TEXT_STYLE_MARK`). Ver C3-bis: no hay `textStyle` en `@react-email/editor`, y el
   * panel de texto del kit escribe en el BLOQUE PADRE.
   *
   * ⛔ Recortado a `EMAIL_DOC_TEXT_STYLE_PROPS`: un `<span>` es inline, y un `padding` ahí se ve
   * distinto en cada cliente de correo. Lo que no aplica se descarta en silencio, igual que en C3.
   */
  textStyle: EmailDocStyle | null
  /**
   * ☠️ ESTE PEDAZO ES UN SALTO DE LÍNEA, NO TEXTO CON UN `\n` ADENTRO.
   *
   * Un `hardBreak` —shift+enter en el editor— se aplana a un segmento cuyo texto es `'\n'`. En una
   * pantalla eso alcanza; en un CORREO no: el HTML colapsa el espacio en blanco, así que el salto
   * salía como un espacio y el renglón que el autor cortó volvía a pegarse.
   *
   * ⚠️ Se descubrió al morir el marco. El caso que lo cubría afirmaba `<br` sobre el HTML entero y
   * pasaba **por el pie del marco**, que trae dos `<br />` en la dirección — no por el salto. Sin
   * pie, el caso se puso rojo y destapó que la afirmación no probaba lo que decía probar.
   *
   * Es una BANDERA y no un `text === '\n'` en cada renderizador: un nodo `text` pegado desde una
   * página web puede traer un `\n` legítimo, y ahí el salto no lo pidió nadie.
   */
  lineBreak?: true
}

const NO_STYLE: EmailDocInlineStyle = {
  bold: false,
  italic: false,
  underline: false,
  strike: false
}

/** Los marks de un nodo de texto, ya filtrados por la lista blanca. */
const marksOf = (node: Record<string, unknown>): Record<string, unknown>[] =>
  Array.isArray(node.marks) ? node.marks.map(asEmailDocRecord) : []

const styleFromMarks = (marks: Record<string, unknown>[]): EmailDocInlineStyle => {
  const has = (type: EmailDocMarkType) => marks.some((mark) => mark.type === type)
  return {
    bold: has('bold'),
    italic: has('italic'),
    underline: has('underline'),
    strike: has('strike')
  }
}

/**
 * El destino de un pedazo de texto: el `href` de su mark `link`, resuelto y filtrado.
 *
 * ⚠️ `target` y `rel` del mark se IGNORAN: los fija el renderizador. Un documento no decide cómo se
 * abre un enlace.
 *
 * ⭐ Pasa por `emailDocHref` y no por `safeUrl` pelado: un enlace de texto puede llevar la misma
 * variable que un botón, y la regla resolver → sanear vale para los dos. Escribirla sólo en el
 * botón habría dejado una puerta donde `{{ expediente.enlace }}` se imprime crudo.
 */
const hrefFromMarks = (
  marks: Record<string, unknown>[],
  variables: Record<string, string>
): string | null => {
  const link = marks.find((mark) => mark.type === 'link')
  return link ? emailDocHref(emailDocAttrs(link).href, variables) : null
}

/** El estilo sano del mark `link`, cuando lo hay. Ver `EmailDocInlineSegment.linkStyle`. */
const styleFromLinkMark = (marks: Record<string, unknown>[]): EmailDocStyle | null => {
  const link = marks.find((mark) => mark.type === 'link')
  return link ? emailDocStyle(emailDocAttrs(link).style) : null
}

/**
 * El estilo sano del mark de TEXTO, cuando lo hay. Ver `EmailDocInlineSegment.textStyle`.
 *
 * ⚠️ Devuelve `null` —y no `{}`— cuando el mark no está, para que los dos renderizadores puedan
 * distinguir «no hay mark» de «el mark no dejó nada dibujable» sin mirar el largo del objeto.
 */
const styleFromTextMark = (marks: Record<string, unknown>[]): EmailDocStyle | null => {
  const mark = marks.find((entry) => entry.type === EMAIL_DOC_TEXT_STYLE_MARK)
  return mark ? emailDocTextStyle(emailDocAttrs(mark).style) : null
}

/**
 * El contenido inline de un nodo, aplanado a pedazos ya resueltos.
 *
 * Aplana RECURSIVO porque un nodo inline puede tener hijos (hoy no los tiene, pero el formato es de
 * la librería y no nuestro), y porque un `hardBreak` es un nodo hermano del texto y no un carácter.
 *
 * ☠️ UN NODO `variable` QUE NO RESUELVE SALE VACÍO, NO CRUDO. Dejar `{{ etapa4.banco }}` en un
 * correo de marca es peor que una frase con un hueco: el hueco se lee como un dato que faltaba, el
 * token se lee como un sistema roto. Es la misma decisión que el formato viejo tomó y por el mismo
 * motivo.
 *
 * ⛔ Un tipo de nodo INLINE fuera de la lista blanca no llega acá con un issue: los issues los
 * asienta el walker de bloques (C4), que es quien conoce la posición. Acá simplemente no aporta
 * texto.
 */
export const emailDocSegments = (
  node: unknown,
  context: EmailDocContext,
  keyPrefix = 'seg'
): EmailDocInlineSegment[] => {
  const out: EmailDocInlineSegment[] = []

  const walk = (children: Record<string, unknown>[], prefix: string): void => {
    for (const [index, child] of children.entries()) {
      if (out.length >= EMAIL_DOC_LIMITS.inline) return
      const key = `${prefix}-${index}`
      const type = String(child.type ?? '')

      if (type === 'text') {
        const text = String(child.text ?? '')
        if (text === '') continue
        const marks = marksOf(child)
        out.push({
          key,
          text,
          style: styleFromMarks(marks),
          href: hrefFromMarks(marks, context.variables),
          linkStyle: styleFromLinkMark(marks),
          textStyle: styleFromTextMark(marks)
        })
        continue
      }

      if (type === 'variable') {
        const token = String(emailDocAttrs(child).variable ?? '')
        const value = context.variables[token] ?? ''
        if (value === '') continue
        const marks = marksOf(child)
        out.push({
          key,
          text: value,
          style: styleFromMarks(marks),
          href: hrefFromMarks(marks, context.variables),
          linkStyle: styleFromLinkMark(marks),
          textStyle: styleFromTextMark(marks)
        })
        continue
      }

      if (type === 'hardBreak') {
        out.push({
          key,
          text: '\n',
          style: NO_STYLE,
          href: null,
          linkStyle: null,
          textStyle: null,
          lineBreak: true
        })
        continue
      }

      walk(emailDocChildren(child), key)
    }
  }

  walk(emailDocChildren(node), keyPrefix)
  return out
}

/**
 * ¿Este documento declara en algún lado un nodo de este tipo?
 *
 * ☠️ EL MARCO NO REPITE LO QUE EL AUTOR YA COLOCÓ. El botón «Ver expediente» lo pone el marco desde
 * siempre; si el cuerpo trae su propio `button`, el marco se calla el suyo y no salen dos. Es la
 * misma derivación que hacía `emailContentDeclares` sobre el AST de BlockNote — sólo que ahora
 * recorre `content` (TipTap) en vez de `children`, y pregunta por el tipo de nodo del editor.
 *
 * ⚠️ Se mira la DECLARACIÓN, no la visibilidad. En v1 no hay condicionales, así que declarado es
 * visible; la distinción se conserva en el nombre para el día que vuelvan.
 */
export const emailDocDeclares = (blocks: unknown, type: EmailDocNodeType): boolean => {
  if (!Array.isArray(blocks)) return false
  let seen = 0
  const walk = (nodes: Record<string, unknown>[]): boolean => {
    for (const node of nodes) {
      if (++seen > EMAIL_DOC_LIMITS.nodes) return false
      if (node.type === type) return true
      if (walk(emailDocChildren(node))) return true
    }
    return false
  }
  return walk(blocks.map(asEmailDocRecord))
}

/**
 * El nivel de un encabezado, 1 a 6.
 *
 * `attrs.level` en TipTap, contra `props.level` en BlockNote. El recorte es el mismo: la barra
 * ofrece los seis y recortarlos a tres borraba la jerarquía.
 */
export const emailHeadingLevelDoc = (node: unknown): 1 | 2 | 3 | 4 | 5 | 6 => {
  const level = Math.round(Number(emailDocAttrs(node).level ?? 2))
  if (!Number.isFinite(level)) return 2
  return Math.min(Math.max(level, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6
}

/**
 * Las alineaciones que el editor produce.
 *
 * ⚠️ SON TRES, no cuatro: el formato viejo tenía `justify` y este editor no lo ofrece. Un
 * `justify` heredado cae al default.
 */
export const EMAIL_DOC_ALIGNMENTS = ['left', 'center', 'right'] as const
export type EmailDocAlign = (typeof EMAIL_DOC_ALIGNMENTS)[number]

export const isEmailDocAlign = (value: unknown): value is EmailDocAlign =>
  typeof value === 'string' && (EMAIL_DOC_ALIGNMENTS as readonly string[]).includes(value)

/**
 * La alineación DECLARADA en los attrs, o `null` cuando el nodo no dice nada.
 *
 * ⚠️ `align` primero y `alignment` después, con `||` y no con `??`, porque es lo que hace el export
 * del editor (`attrs.align || attrs.alignment`) y porque un documento real guarda
 * `alignment: null` cuando el autor no eligió: `null` tiene que caer al siguiente, no quedarse.
 *
 * ⛔ Devuelve `null` —y no `'left'`— para lo ausente y para lo inválido, porque «no eligió» y
 * «eligió izquierda» se resuelven DISTINTO cuando la cadena de estilo dice algo: el primero deja
 * pasar el `text-align` del `style`, el segundo lo tapa.
 */
export const emailDocAlignAttr = (node: unknown): EmailDocAlign | null => {
  const attrs = emailDocAttrs(node)
  const declared = attrs.align || attrs.alignment
  return (EMAIL_DOC_ALIGNMENTS as readonly unknown[]).includes(declared)
    ? (declared as EmailDocAlign)
    : null
}

/**
 * La alineación que hay que ESCRIBIR, o `undefined` cuando no hay nada que escribir.
 *
 * Es la regla de C2, en código, y es la ÚNICA fuente de alineación de todo el formato:
 *
 * | attrs | `text-align` del style | sale |
 * |---|---|---|
 * | `center` | lo que sea | `center` — el attr explícito manda |
 * | `left` | `right` | nada — se ve igual que el default, y tapa al style, como el editor |
 * | ausente / inválido | `center` | `center` — el `style` sobrevive |
 * | ausente | ausente | nada |
 *
 * ☠️ Que `left` no escriba nada está medido en el formato viejo y vale igual acá: emitir
 * `text-align:left` en cada párrafo cambia el HTML de todos los avisos que nadie tocó. Lo que no se
 * eligió no se escribe — y lo que se eligió igual al default, tampoco.
 */
export const emailDocAlignStyle = (node: unknown): EmailDocAlign | undefined => {
  const declared = emailDocAlignAttr(node)
  if (declared !== null) return declared === 'left' ? undefined : declared
  const fromStyle = emailDocNodeStyle(node).textAlign
  return fromStyle === 'center' || fromStyle === 'right' ? fromStyle : undefined
}

/**
 * La alineación EFECTIVA, con su default. La usan los que alinean con márgenes —imagen y botón—,
 * que no pueden dejar el dato en `undefined`.
 *
 * Se deriva de `emailDocAlignStyle` a propósito: una sola resolución, dos formas. Si algún día
 * cambia la regla, cambia en un solo lugar.
 */
export const emailDocAlign = (node: unknown): EmailDocAlign => emailDocAlignStyle(node) ?? 'left'

/**
 * El número con el que arranca una lista ordenada. `attrs.start`, 1 por defecto.
 *
 * Se acota a positivo: un `start` negativo o cero no lo produce el editor y lo pintan distinto
 * cliente a cliente.
 */
export const emailDocListStart = (node: unknown): number => {
  const start = Math.round(Number(emailDocAttrs(node).start ?? 1))
  return Number.isFinite(start) && start > 0 ? start : 1
}

/* ────────────────────────────────────────────────────────────────────────────
 * LAS MEDIDAS DE LA MAQUETACIÓN (carril G)
 *
 * Los tres que siguen leen ATRIBUTOS, no el `style`, y por eso no los cubre C3: un atributo no
 * pasa por `emailDocStyle` y termina igual dentro del HTML que recibe un cliente. Valen la misma
 * vara —lista blanca de formas, no lista negra— por el mismo motivo que `safeUrl` y `safeColor`.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Un ancho de maquetación: `'580'`, `'240px'` o `'50%'`. Positivo y acotado.
 *
 * ⚠️ El editor guarda estos anchos SIN unidad cuando vienen del atributo HTML (`width="580"`) y
 * CON unidad cuando vienen del panel de tamaño, que escribe en el `style`. Las dos formas entran.
 */
const LAYOUT_WIDTH = /^(\d+(?:\.\d+)?)(px|%)?$/

const safeLayoutWidth = (value: unknown): { amount: number; unit: string; raw: string } | null => {
  const raw =
    typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : ''
  const match = LAYOUT_WIDTH.exec(raw)
  if (!match) return null
  const amount = Number(match[1])
  if (!Number.isFinite(amount) || amount <= 0) return null
  const unit = match[2] ?? ''
  const max = unit === '%' ? 100 : LENGTH_UNIT_MAX.px
  return amount <= max ? { amount, unit, raw } : null
}

/**
 * El ancho que va al ATRIBUTO `width` de una `<table>`, o `undefined`.
 *
 * ☠️ Mira el attr Y el `style`, en ese orden, porque el serializador del editor hace exactamente
 * eso: `node.type === 'table' && inlineStyles.width && !node.attrs?.width` promueve el ancho de la
 * cadena de estilo al atributo antes de dibujar (`core/serializer/compose-react-email.tsx`). Sin la
 * promoción, una tabla con `width: 100%` en el estilo sale angosta en los clientes que ignoran el
 * CSS de la tabla y ancha en la vista previa: el WYSIWYG mentiroso otra vez.
 *
 * ⚠️ Un `width: auto` o `fit-content` del estilo NO se promueve —no son valores de ese atributo—:
 * quedan donde estaban, en el `style`, que sí los entiende.
 */
export const emailDocTableWidth = (node: unknown): string | undefined => {
  const fromAttr = safeLayoutWidth(emailDocAttrs(node).width)
  if (fromAttr) return fromAttr.raw
  return safeLayoutWidth(emailDocNodeStyle(node).width)?.raw
}

/**
 * El ancho que va al `style` de una COLUMNA, ya con unidad, o `undefined`.
 *
 * Sale del attr `width` y se escribe DESPUÉS del estilo del nodo, que es el orden del export del
 * editor (`{ ...style, ...inlineStyles, ...(width ? { width } : {}) }`, `extensions/columns.tsx`).
 *
 * ⚠️ Un número pelado se completa a `px`: acá el destino es una propiedad CSS, y `width: 240` en
 * una hoja de estilo no lo dibuja nadie. Es la misma regla que `safeLength` aplica al resto.
 */
export const emailDocColumnWidth = (node: unknown): string | undefined => {
  const width = safeLayoutWidth(emailDocAttrs(node).width)
  if (!width) return undefined
  return width.unit === '' ? `${width.amount}px` : width.raw
}

/** Tope de la separación entre columnas. Arriba de esto ya no es una maqueta, es un error. */
const CELL_SPACING_MAX = 100

/**
 * La separación entre columnas, tal como viaja al correo: el atributo `cellspacing` de la tabla.
 *
 * ☠️ Es un ATTR y no un estilo, y lo escribe su propio panel del Inspector —un `NumberInput` en px
 * (`ui/inspector/sections/column-spacing.tsx`)—. En el lienzo el editor la dibuja como `gap` del
 * CSS; en el correo tiene que ser `cellspacing`, porque `gap` no lo sostiene ningún cliente de
 * correo sobre una tabla.
 *
 * `undefined` cuando no hay separación —ausente, vacía o `0`—, igual que `getRowCellSpacing` del
 * editor: emitir `cellspacing="0"` es lo mismo que el default y ensucia el HTML de toda columna.
 *
 * ⛔ Y a diferencia del editor, que devuelve la cadena TAL CUAL, acá se valida: un `cellspacing`
 * pegado desde HTML entra sin pasar por el `NumberInput`, y lo que sale de acá se escribe crudo en
 * un atributo del correo.
 */
export const emailDocCellSpacing = (node: unknown): string | undefined => {
  const raw = emailDocAttrs(node).cellspacing
  const value = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim())
  if (!Number.isInteger(value) || value <= 0 || value > CELL_SPACING_MAX) return undefined
  return String(value)
}

/* ────────────────────────────────────────────────────────────────────────────
 * C6 — EL ESTILO DE PÁGINA, QUE ES DEL CORREO Y NO DEL NODO
 *
 * Hasta la tanda 1 el envoltorio era del CÓDIGO: el ancho, el fondo y el relleno del correo vivían
 * en `styles/shared` y no había forma de que dos avisos se vieran distinto por fuera de la hoja.
 * `pageStyle` es lo que abre eso, y es lo ÚLTIMO que quedaba cerrado.
 *
 * ── ☠️ LOS DOS NOMBRES ESTÁN CRUZADOS, Y HAY QUE LEERLO DOS VECES ────────────
 *
 * El panel del editor —heredando el Inspector de Resend— llama:
 *
 *  · **Página** a la superficie de ATRÁS: el gris que rodea al correo. En React Email es `<Body>`.
 *  · **Cuerpo** a la HOJA: el rectángulo blanco donde se lee el correo. Es el `<Container>`.
 *
 * Es contraintuitivo, y las CLAVES de este objeto son las de la referencia (`page` / `body`) porque
 * son dato y no copy — lo que está en español es lo que se lee en pantalla. Lo que sí se puede es
 * que la traducción viva en UN lugar —acá—, y que lo que sale tenga el nombre del elemento que
 * decora (`body` / `container`) y no el de la etiqueta del panel.
 *
 * ── ⛔ NO ES `globalContent`, Y LA DISTANCIA ES EL PUNTO ─────────────────────
 *
 * `globalContent` —el tema del editor, 7 KB por documento— sigue EXCLUIDO del schema por las
 * razones de siempre: pesa, congela la marca en cada aviso y no lo escribió nadie a propósito.
 * `pageStyle` es lo contrario: un objeto EXPLÍCITO de trece campos que alguien eligió en un panel,
 * y cada uno pasa por el mismo validador que un `style` de nodo. No hay «CSS global» que redactar
 * —un campo de texto libre que termina dentro de un `<style>` es un vector de inyección— y por eso
 * el panel tampoco lo ofrece.
 *
 * ── LA VARA ES LA MISMA QUE LA DE C3 ─────────────────────────────────────────
 *
 * ⭐ Cada campo se valida con `EMAIL_DOC_STYLE_PROPS`, la lista blanca que ya existe, y no con
 * reglas nuevas: un color pasa por `safeColor`, una longitud por el tope de su unidad. Escribir un
 * segundo validador «parecido» habría sido fabricar la divergencia que este archivo entero existe
 * para evitar — y en el campo que decide de qué color sale un correo de marca.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * El estilo de PÁGINA de un correo, tal como lo guarda el editor.
 *
 * Todo opcional, y lo que falta lo pone `EMAIL_DOC_PAGE_STYLE_DEFAULTS`: un correo sin `pageStyle`
 * sale con los defaults NEUTROS, no con lo que cada superficie tenga a mano.
 */
export type EmailDocPageStyle = {
  /** La superficie de atrás. En el panel se llama «Página»; en el correo es `<Body>`. */
  page?: {
    background?: string
    padding?: string
  }
  /** La hoja. En el panel se llama «Body»; en el correo es `<Container>`. */
  body?: {
    align?: EmailDocAlign
    textColor?: string
    background?: string
    width?: string
    height?: string
    padding?: string
    margin?: string
    borderRadius?: string
    borderWidth?: string
    borderColor?: string
  }
}

/** Lo que los dos renderizadores dibujan: el estilo del `<Body>` y el del `<Container>`. */
export type EmailDocPageStyles = {
  body: EmailDocStyle
  container: EmailDocStyle
}

/**
 * ⭐ LOS DEFAULTS. UNA fuente para el servidor, la vista previa, el lienzo y el panel.
 *
 * ── ☠️ HABÍA CUATRO «DE FÁBRICA», Y NINGUNO SABÍA DE LOS OTROS ──────────────
 *
 * Antes de esto, un aviso sin `pageStyle` se dibujaba distinto en cada superficie porque cada una
 * traía su propio fallback. MEDIDO en el navegador, con el panel vacío:
 *
 *  | | lienzo (tema del kit) | vista previa (Tailwind) | correo (`styles/shared`) |
 *  |---|---|---|---|
 *  | relleno de la hoja | `24px 40px 40px 40px` | `40px` | `40px` |
 *  | margen de la hoja | `0` | `30px auto` | `30px auto` |
 *  | color del texto | `#000000` | `#051938` | heredado (negro) |
 *
 * Tres lecturas del MISMO objeto vacío. Eso es lo que se reportaba como «los estilos no se
 * aplican»: no fallaba el que el autor elegía, fallaba el que NO elegía.
 *
 * Con esta constante ese hueco desaparece por construcción: `emailDocPageStyle` devuelve las trece
 * propiedades SIEMPRE, así que las tres superficies dibujan un valor explícito y ninguna llega a
 * consultar su propio fallback. El día que alguien agregue un campo al panel y se olvide del
 * default, el spec de abajo se pone rojo.
 *
 * ── POR QUÉ NEUTROS Y NO LOS DE NUESTRA MARCA ───────────────────────────────
 *
 * Un correo en blanco no es un correo de Créditos: es un correo. Los valores de marca —el gris
 * `#EFEEF1`, la hoja de 580, el relleno de 40— son de las PLANTILLAS del catálogo, que los traen
 * en su propio `pageStyle` (`credit-email-template-catalog.ts`). Meterlos acá dejaría a cualquier
 * documento nuevo con una marca que nadie eligió, y haría imposible partir de una hoja limpia.
 *
 * Los valores son los de la referencia (Resend, `referencia-resend-page-style-neutral.png`):
 * página blanca sin relleno, hoja blanca de 600 px centrada, texto negro, sin relleno, sin radio y
 * sin borde.
 *
 * ⚠️ `borderColor` viaja aunque `borderWidth` sea `0px`: es el color que el panel muestra y el que
 * se va a usar en cuanto alguien le dé grosor. Un borde de cero no dibuja nada.
 */
export const EMAIL_DOC_PAGE_STYLE_DEFAULTS = {
  page: {
    background: '#ffffff',
    padding: '0px'
  },
  body: {
    align: 'center',
    textColor: '#000000',
    background: '#ffffff',
    width: '600px',
    height: 'auto',
    padding: '0px',
    margin: '0px',
    borderRadius: '0px',
    borderWidth: '0px',
    borderColor: '#000000'
  }
} as const satisfies EmailDocPageStyle

/**
 * El estilo de página con TODOS los campos resueltos: lo que el autor eligió, o el default.
 *
 * Es lo que el PANEL muestra —campos con valores, no placeholders grises— y sale de la misma
 * constante que dibuja. Un panel que resolviera los defaults por su cuenta sería el quinto «de
 * fábrica», que es justo lo que esta tanda vino a cerrar.
 */
export type EmailDocPageStyleValues = {
  page: Required<NonNullable<EmailDocPageStyle['page']>>
  body: Required<NonNullable<EmailDocPageStyle['body']>>
}

/**
 * ⭐ LA TABLA: campo del panel → propiedad CSS que lo dibuja. Es la traducción, y es UNA.
 *
 * De acá salen las dos mitades —lo que se dibuja y lo que se rechaza al guardar— y por eso el
 * schema no necesita una copia: preguntarle a esta función qué se cayó es más barato y no puede
 * separarse. `align` no está en la tabla porque no es una propiedad: son dos márgenes, y su regla
 * vive abajo.
 */
const EMAIL_DOC_PAGE_STYLE_FIELDS = {
  page: {
    background: 'backgroundColor',
    padding: 'padding'
  },
  body: {
    textColor: 'color',
    background: 'backgroundColor',
    width: 'width',
    height: 'height',
    padding: 'padding',
    margin: 'margin',
    borderRadius: 'borderRadius',
    borderWidth: 'borderWidth',
    borderColor: 'borderColor'
  }
} as const satisfies Record<string, Record<string, EmailDocStyleProp>>

/** Las dos secciones del panel, para recorrerlas sin escribirlas dos veces. */
const EMAIL_DOC_PAGE_SECTIONS = ['page', 'body'] as const

/** Tope de un valor del panel. Un color o una medida no llegan ni cerca; una inyección, sí. */
const PAGE_STYLE_VALUE_MAX = 200

/**
 * Un valor del panel → el valor SANO de esa propiedad, o nada.
 *
 * Se apoya en `EMAIL_DOC_STYLE_PROPS` para no tener una segunda idea de qué es un color o una
 * longitud dibujable, y repite el corte de `UNSAFE_STYLE_VALUE` porque acá el valor no viene de una
 * cadena CSS: viene de un campo del panel, y ese campo también es texto libre.
 */
const safePageValue = (prop: EmailDocStyleProp, value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (raw === '' || raw.length > PAGE_STYLE_VALUE_MAX || UNSAFE_STYLE_VALUE.test(raw)) return null
  return safeStyleValue(EMAIL_DOC_STYLE_PROPS[prop], raw)
}

/**
 * El estilo de página → los dos estilos que el correo dibuja.
 *
 * **Lo consumen el servidor y la vista previa, y es LA MISMA función**: es la única forma de que lo
 * que se ve en el editor sea lo que llega a la bandeja. Devuelve objetos vacíos cuando no hay nada
 * que decir, así que el llamador siempre puede hacer `{ ...suBase, ...esto }`.
 *
 * ── TRES DECISIONES QUE NO SE VEN EN LA FIRMA ────────────────────────────────
 *
 * 1. **La alineación se escribe con márgenes, y va DESPUÉS del `margin`.** No hay `text-align` que
 *    centre un contenedor de correo: se centra con `margin-left/right: auto`, que es lo que hace
 *    `<Container>` de fábrica. Y se aplica al final a propósito: el `margin` del panel es el
 *    shorthand de los cuatro lados y los tres botones de alineación son el control DEDICADO —quien
 *    aprieta «centrar» espera que centre, aunque antes haya escrito un margen—.
 * 2. **Un ancho de borde sin trazo no dibuja nada**, así que un `borderWidth` presente arrastra
 *    `borderStyle: 'solid'`. El panel no ofrece elegir el trazo (la captura tiene «Border» y
 *    «Border color», nada más) y emitir sólo el ancho habría sido un control que no hace nada.
 * 3. **`textColor` sale como `color`.** El nombre del panel habla del texto; la propiedad CSS que
 *    lo pinta —y que HEREDAN los párrafos de adentro— es `color`.
 * 4. **Lo que el autor no eligió lo pone el DEFAULT, campo por campo.** El `pageStyle` guardado se
 *    FUSIONA sobre `EMAIL_DOC_PAGE_STYLE_DEFAULTS`, no lo reemplaza en bloque: elegir un ancho no
 *    puede dejar sin fondo a la hoja. Y el default pasa por el MISMO validador que el valor del
 *    autor —no se cuela por un atajo—, así que un default indibujable se cae acá y el spec lo ve.
 */
export const emailDocPageStyle = (value: unknown): EmailDocPageStyles => {
  const source = asEmailDocRecord(value)
  const out: EmailDocPageStyles = { body: {}, container: {} }

  for (const section of EMAIL_DOC_PAGE_SECTIONS) {
    const values = asEmailDocRecord(source[section])
    const defaults: Record<string, string> = EMAIL_DOC_PAGE_STYLE_DEFAULTS[section]
    const target = section === 'page' ? out.body : out.container
    for (const [field, prop] of Object.entries(EMAIL_DOC_PAGE_STYLE_FIELDS[section])) {
      const safe = safePageValue(prop, values[field]) ?? safePageValue(prop, defaults[field])
      if (safe !== null) target[prop] = safe
    }
  }

  if (out.container.borderWidth !== undefined) out.container.borderStyle = 'solid'

  const raw = asEmailDocRecord(source.body).align
  const align = isEmailDocAlign(raw) ? raw : EMAIL_DOC_PAGE_STYLE_DEFAULTS.body.align
  out.container.marginLeft = align === 'left' ? '0' : 'auto'
  out.container.marginRight = align === 'right' ? '0' : 'auto'

  return out
}

/**
 * El estilo de página tal como lo tiene que MOSTRAR el panel: el valor del autor, o el default.
 *
 * ⚠️ Devuelve el valor CRUDO que el autor escribió, sin sanear: el campo tiene que mostrar lo que
 * la persona tipeó aunque todavía no sea dibujable —está a media palabra—, y quien decide si se
 * guarda es `emailDocPageStyleRejections` al guardar. Sanear acá haría que el texto saltara solo
 * mientras se escribe.
 */
export const emailDocPageStyleValues = (value: unknown): EmailDocPageStyleValues => {
  const source = asEmailDocRecord(value)
  const pick = <Section extends 'page' | 'body'>(section: Section) => {
    const values = asEmailDocRecord(source[section])
    const defaults: Record<string, string> = EMAIL_DOC_PAGE_STYLE_DEFAULTS[section]
    const out: Record<string, string> = {}
    for (const field of Object.keys(defaults)) {
      const raw = values[field]
      out[field] = typeof raw === 'string' && raw.trim() !== '' ? raw : defaults[field]
    }
    return out
  }

  return {
    page: pick('page') as EmailDocPageStyleValues['page'],
    body: pick('body') as EmailDocPageStyleValues['body']
  }
}

/**
 * ☠️ LO QUE EL ESTILO DE PÁGINA **NO** PUDO DIBUJAR, POR SU RUTA.
 *
 * Es la mitad que el SCHEMA consume, y existe para que no haya dos varas. Un `style` de nodo que
 * trae basura se cae en silencio —es un adorno perdido dentro de un documento que igual sale—,
 * pero `pageStyle` es un objeto que alguien acaba de guardar desde un panel: si el color que
 * eligió no se va a dibujar, tiene que enterarse AHORA y no cuando le llegue el correo gris.
 *
 * Devuelve rutas (`['body', 'width']`) y no mensajes: quién las convierte en un issue de Zod es el
 * schema, que es el que sabe en qué `path` está parado.
 *
 * ⚠️ Una clave que el panel NO ofrece se ignora sin protestar: es dato viejo o de más, no un valor
 * que alguien quiso ver dibujado. Lo que se reporta es lo que SÍ es un campo del panel y no pasó.
 */
export const emailDocPageStyleRejections = (value: unknown): string[][] => {
  const source = asEmailDocRecord(value)
  const rejected: string[][] = []

  for (const section of EMAIL_DOC_PAGE_SECTIONS) {
    const values = asEmailDocRecord(source[section])
    for (const [field, prop] of Object.entries(EMAIL_DOC_PAGE_STYLE_FIELDS[section])) {
      const raw = values[field]
      if (raw === undefined || raw === null) continue
      if (safePageValue(prop, raw) === null) rejected.push([section, field])
    }
  }

  const align = asEmailDocRecord(source.body).align
  if (align !== undefined && align !== null && !isEmailDocAlign(align)) {
    rejected.push(['body', 'align'])
  }

  return rejected
}

/* ────────────────────────────────────────────────────────────────────────────
 * C4 — EL RENDERIZADOR DE CORREO (carril C). Contrato, no implementación.
 *
 * `packages/email/src/components/email-doc-content.tsx` exporta:
 *
 *   export const EMAIL_DOC_RENDERERS: Record<
 *     EmailDocNodeType,
 *     (node, context: EmailDocContext, renderChildren: () => React.ReactNode[]) => React.ReactNode
 *   >
 *   export const EmailDocContent = ({ blocks, context }: {
 *     blocks: Record<string, unknown>[]
 *     context: EmailDocContext
 *   }) => React.ReactNode
 *
 * REGISTRO, no cadena de `if`: agregar un tipo es agregar un renglón a `EMAIL_DOC_NODE_TYPES` y su
 * entrada acá. El registro se exporta para que la vista previa del editor (carril B) pueda afirmar
 * que cubre las mismas claves sin importar el paquete — comparar `Object.keys` es barato y no
 * arrastra React Email al bundle del navegador (ADR 0030).
 *
 * Reglas que C4 NO puede cambiar por su cuenta:
 *  - Los primitivos son los de React Email que el resto del paquete ya usa —`Text`, `Heading`,
 *    `Hr`, `Img`, `Section`, `Link`— con los estilos compartidos `linkButton`/`link`/`paragraph`
 *    de `../styles/shared`. El botón es un `Link` con `linkButton`, NO el `Button` de
 *    `@react-email/components`: ese paquete NO se sube de versión en este carril.
 *  - Listas REALES: un `bulletList` es UN `<ul>` con sus `<li>` adentro (con `start` en el `<ol>`),
 *    no un `<ul>` por ítem. Era la deuda de BlockNote, que guardaba cada ítem como bloque hermano
 *    y obligaba a abrir una lista por renglón.
 *  - `container` es transparente: dibuja hijos y NADA más. Un `Section` de React Email es una
 *    tabla; meter una porque el editor forzó un nodo estructural cambiaría el HTML de todos los
 *    avisos sin que nadie lo pidiera.
 *  - **El estilo del nodo se FUSIONA sobre el estilo base del tipo, prop a prop**
 *    (`{ ...base, ...emailDocNodeStyle(node) }`), que es el orden del export del editor. La
 *    alineación se escribe DESPUÉS de la fusión, con `emailDocAlignStyle`, porque es la única prop
 *    con dos orígenes y su regla ya está resuelta acá.
 *  - `image.alignment` SE HONRA (margin auto / 0), aunque el editor no lo exporte: el nodo lo
 *    persiste y el compilador es nuestro. Sus márgenes se escriben DESPUÉS del estilo del nodo, y
 *    sólo cuando el nodo DECLARA alineación (`emailDocAlignAttr`): así el attr explícito manda, y
 *    un `margin: 0 auto` pegado sobrevive cuando no hay attr. Es la misma regla que la del
 *    `text-align`, aplicada al eje que la imagen y el botón usan para alinearse.
 *  - **La MAQUETACIÓN se dibuja con tablas, y su semántica es la del editor** (carril G):
 *    `section` → `Section`; `twoColumns`/`threeColumns`/`fourColumns` → una fila de tabla con sus
 *    `<td>` HERMANOS y el `cellspacing` del nodo; `columnsColumn` y `tableCell` → `Column`;
 *    `table` → una tabla de datos real (`role="presentation"`, `border=0`, `cellpadding=0`,
 *    `cellspacing=0` — los tres FIJOS, como en `extensions/table.tsx`) con su `<tbody>` y sus
 *    `<tr>`. El ancho de la tabla sale de `emailDocTableWidth`, el de la columna de
 *    `emailDocColumnWidth`, la separación de `emailDocCellSpacing`.
 *  - ☠️ **Los párrafos DENTRO de una celda o de una columna van apretados**, con el mismo
 *    `margin: 0` que ya usan `listItem` y `blockquote`. No es estética: el `<p>` de React Email
 *    trae `margin: 16px 0` y una ficha de datos de cuatro filas sale con el doble de alto que el
 *    correo que replica. El editor emite `margin: 0` en TODO párrafo (su tema arranca del `reset`,
 *    `plugins/email-theming/themes.ts`), así que acá se hace lo mismo donde importa.
 *  - Un tipo fuera de la lista blanca asienta `BLOCK_UNSUPPORTED` con su `type` en el detalle y
 *    devuelve `null`. Un `src` que no pasa `safeUrl`, `IMAGE_REJECTED`. Un `button` cuyo `href` no
 *    pasa, `BUTTON_REJECTED`. Ninguno corta el envío.
 *  - ⚠️ Un `columnsColumn` o un `tableCell` SUELTO —fuera de su padre— se dibuja igual, como el
 *    `<td>` que es. No revienta ni se descarta: el registro no conoce el contexto, y un documento
 *    torcido tiene que salir feo, no vacío.
 *  - El corte por `EMAIL_DOC_LIMITS.depth` devuelve `null` en silencio; el corte por
 *    `EMAIL_DOC_LIMITS.nodes` lo hace el schema al guardar, no el render.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * C5 — EL SENDER (carril C). Contrato, no implementación.
 *
 * El adaptador de correo pasa al ENVOLTORIO —uno solo, `credit-notice.tsx`—:
 *
 *   content: message.content?.blocks ?? null
 *   pageStyle: message.content?.pageStyle ?? null     // → `emailDocPageStyle`
 *   context: {
 *     variables,                       // las del composer, ya resueltas a texto legible
 *     onIssue                          // a la bitácora del envío
 *   } satisfies EmailDocContext
 *
 * ── ⭐ LO QUE LA TANDA 1 SE LLEVÓ, Y POR QUÉ ────────────────────────────────
 *
 * Había TRES marcos —interno, cliente, aprobación— y cada uno inyectaba por su cuenta el logo, la
 * franja de tres columnas, la ficha del expediente, el botón «Ver expediente» y el pie. Nada de eso
 * era editable, así que el editor prometía un correo y mandaba otro: se podía redactar el medio y
 * no los bordes.
 *
 * Ahora el envoltorio pone `Html/Head/Preview/Body/Container`, la tipografía y `pageStyle`, y
 * ADENTRO va el documento y nada más. El encabezado, la ficha y el botón son PIEZAS DEL CATÁLOGO
 * del editor: contenido que se compone, se mueve y se borra como cualquier párrafo.
 *
 * Con eso desaparecen tres derivaciones que este archivo sostenía:
 *
 *  · `emailDocDeclares(blocks, 'button')` ya no decide nada en el envío —no hay botón que el marco
 *    pueda repetir—. La función sigue viva: el editor la usa para saber qué trae un documento.
 *  · `context.creditUrl` y `context.dossierRows` no existen más. El enlace y las filas de la ficha
 *    entran como VARIABLES (`expediente.enlace`, `expediente.codigo`, …), que es la única puerta
 *    por la que el autor podía usarlas.
 *  · El formato (`layout: internal | client`) MURIÓ. La audiencia dejó de ser un `select` y pasó a
 *    ser una plantilla del catálogo: «Aviso interno» trae el logo de IDC, «Aviso al cliente» la
 *    marca de Condado Naranjo. Un `select` con dos valores no podía representar un tercer formato
 *    sin tocar el código; una plantilla sí.
 *
 * `content: null` → el envoltorio cae a `body` en texto plano, que es el camino de los avisos que
 * todavía no se recrearon. ⚠️ Y ahí sale SIN encabezado ni pie: el marco que se los ponía ya no
 * está. Recrear esos avisos con las plantillas del catálogo es parte del despliegue, no del
 * código.
 * ──────────────────────────────────────────────────────────────────────────── */
